import React, { useEffect, useMemo, useState } from 'react';
import { useUsersStore } from '../../../../store/usersStore';
import { User, UserRole } from '../../../../services/usersApi';
import styles from './UserModal.module.css';

/**
 * Shared Add / Edit user modal — admins capture the full profile fields up
 * front so the data shown on each role's Profile screen (phone, employee id,
 * territory, department, assigned warehouse) is populated rather than the
 * hardcoded placeholders the app used to ship with.
 *
 * Customers are intentionally excluded from the role dropdown because they
 * have a dedicated onboarding flow.
 */

const STAFF_ROLES: UserRole[] = ['Admin', 'Sales Rep', 'Driver', 'Warehouse Manager', 'Call Center'];

interface UserModalProps {
  open: boolean;
  mode: 'add' | 'edit';
  existing?: User | null;
  onClose: () => void;
}

interface FormState {
  full_name: string;
  email: string;
  password: string;          // only for 'add' mode
  role: UserRole;
  phone: string;
  employee_id: string;
  department: string;
  territory: string;
  warehouse_code: string;
  notes: string;
}

const emptyForm = (role: UserRole = 'Sales Rep'): FormState => ({
  full_name: '',
  email: '',
  password: '',
  role,
  phone: '',
  employee_id: '',
  department: '',
  territory: '',
  warehouse_code: '',
  notes: '',
});

export const UserModal: React.FC<UserModalProps> = ({ open, mode, existing, onClose }) => {
  const [form, setForm] = useState<FormState>(emptyForm());
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const create = useUsersStore((s) => s.create);
  const update = useUsersStore((s) => s.update);

  // Role-specific helper labels for the dynamic profile section
  const dynamic = useMemo(() => roleSpecificFields(form.role), [form.role]);

  useEffect(() => {
    if (!open) {
      setError(null);
      setSubmitting(false);
      return;
    }
    if (mode === 'edit' && existing) {
      setForm({
        full_name: existing.full_name ?? '',
        email: existing.email ?? '',
        password: '',
        role: existing.role,
        phone: existing.phone ?? '',
        employee_id: existing.employee_id ?? '',
        department: existing.department ?? '',
        territory: existing.territory ?? '',
        warehouse_code: existing.warehouse_code ?? '',
        notes: existing.notes ?? '',
      });
    } else {
      setForm(emptyForm());
    }
  }, [open, mode, existing]);

  if (!open) return null;

  const onChange = (field: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm((p) => ({ ...p, [field]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim() || !form.role) {
      setError('Full name, email, and role are required'); return;
    }
    if (mode === 'add' && form.password.length < 12) {
      setError('Password must be at least 12 characters'); return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const profile = {
        phone: form.phone.trim() || null,
        employee_id: form.employee_id.trim() || null,
        department: form.department.trim() || null,
        territory: form.territory.trim() || null,
        warehouse_code: form.warehouse_code.trim() || null,
        notes: form.notes.trim() || null,
      };
      if (mode === 'add') {
        await create({
          full_name: form.full_name.trim(),
          email: form.email.trim().toLowerCase(),
          password: form.password,
          role: form.role,
          ...Object.fromEntries(
            Object.entries(profile).filter(([, v]) => v !== null)
          ) as any,
        });
      } else if (existing) {
        await update(existing.id, {
          full_name: form.full_name.trim(),
          email: form.email.trim().toLowerCase(),
          role: form.role,
          ...profile,
        });
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save user');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose} role="dialog" aria-modal="true">
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <header className={styles.header}>
          <h2 className={styles.title}>{mode === 'add' ? 'Add User' : 'Edit User'}</h2>
          <button className={styles.close} onClick={onClose} aria-label="Close">×</button>
        </header>

        <form onSubmit={handleSubmit} className={styles.form}>
          {error && <p className={styles.error}>{error}</p>}

          <h3 className={styles.sectionTitle}>Identity</h3>
          <div className={styles.row}>
            <label className={styles.field}>
              <span>Full Name <span className={styles.required}>*</span></span>
              <input type="text" value={form.full_name} onChange={onChange('full_name')} required maxLength={120} autoFocus />
            </label>
            <label className={styles.field}>
              <span>Role <span className={styles.required}>*</span></span>
              <select value={form.role} onChange={onChange('role')} required>
                {STAFF_ROLES.map((r) => (<option key={r} value={r}>{r}</option>))}
              </select>
            </label>
          </div>

          <div className={styles.row}>
            <label className={styles.field}>
              <span>Email <span className={styles.required}>*</span></span>
              <input type="email" value={form.email} onChange={onChange('email')} required maxLength={120} />
            </label>
            <label className={styles.field}>
              <span>Phone</span>
              <input type="tel" value={form.phone} onChange={onChange('phone')} maxLength={40} placeholder="+1 555 123 4567" />
            </label>
          </div>

          {mode === 'add' && (
            <label className={styles.field}>
              <span>Password <span className={styles.required}>*</span></span>
              <input
                type="password"
                value={form.password}
                onChange={onChange('password')}
                required
                minLength={12}
                autoComplete="new-password"
                placeholder="Minimum 12 characters"
              />
              <span className={styles.hint}>User can change this from their profile after first login.</span>
            </label>
          )}

          <h3 className={styles.sectionTitle}>Work Profile</h3>
          <div className={styles.row}>
            <label className={styles.field}>
              <span>Employee ID</span>
              <input type="text" value={form.employee_id} onChange={onChange('employee_id')} maxLength={40} placeholder={dynamic.employeeIdPlaceholder} />
            </label>
            <label className={styles.field}>
              <span>Department</span>
              <input type="text" value={form.department} onChange={onChange('department')} maxLength={120} placeholder={dynamic.departmentPlaceholder} />
            </label>
          </div>

          {dynamic.showTerritory && (
            <label className={styles.field}>
              <span>{dynamic.territoryLabel}</span>
              <input type="text" value={form.territory} onChange={onChange('territory')} maxLength={120} placeholder={dynamic.territoryPlaceholder} />
            </label>
          )}

          {dynamic.showWarehouse && (
            <label className={styles.field}>
              <span>Assigned Warehouse Code</span>
              <input type="text" value={form.warehouse_code} onChange={onChange('warehouse_code')} maxLength={40} placeholder="e.g. WH-A" />
            </label>
          )}

          <label className={styles.field}>
            <span>Notes (Internal)</span>
            <textarea value={form.notes} onChange={onChange('notes')} rows={2} maxLength={500} placeholder="Any internal notes about this user" />
          </label>

          <div className={styles.actions}>
            <button type="button" className={styles.cancel} onClick={onClose} disabled={submitting}>Cancel</button>
            <button type="submit" className={styles.submit} disabled={submitting}>
              {submitting ? 'Saving…' : mode === 'add' ? 'Create User' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface DynamicConfig {
  showTerritory: boolean;
  showWarehouse: boolean;
  territoryLabel: string;
  territoryPlaceholder: string;
  employeeIdPlaceholder: string;
  departmentPlaceholder: string;
}

function roleSpecificFields(role: UserRole): DynamicConfig {
  switch (role) {
    case 'Driver':
      return {
        showTerritory: true, showWarehouse: false,
        territoryLabel: 'Assigned Territory',
        territoryPlaceholder: 'e.g. North Cincinnati',
        employeeIdPlaceholder: 'e.g. DRV-001',
        departmentPlaceholder: 'Logistics & Delivery',
      };
    case 'Warehouse Manager':
      return {
        showTerritory: false, showWarehouse: true,
        territoryLabel: 'Region',
        territoryPlaceholder: '',
        employeeIdPlaceholder: 'e.g. WH-001',
        departmentPlaceholder: 'Warehouse Operations',
      };
    case 'Sales Rep':
      return {
        showTerritory: true, showWarehouse: false,
        territoryLabel: 'Assigned Territory',
        territoryPlaceholder: 'e.g. South Cincinnati',
        employeeIdPlaceholder: 'e.g. SR-001',
        departmentPlaceholder: 'Sales',
      };
    case 'Call Center':
      return {
        showTerritory: false, showWarehouse: false,
        territoryLabel: '',
        territoryPlaceholder: '',
        employeeIdPlaceholder: 'e.g. SUP-001',
        departmentPlaceholder: 'Customer Support',
      };
    case 'Admin':
    default:
      return {
        showTerritory: false, showWarehouse: false,
        territoryLabel: '',
        territoryPlaceholder: '',
        employeeIdPlaceholder: 'e.g. ADM-001',
        departmentPlaceholder: 'Administration',
      };
  }
}

export default UserModal;
